import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, ArrowRight, Tag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";
import LazyImage from "@/components/LazyImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("Todos");

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setArticles(response.data);
    } catch (error) {
      // handled silently
    } finally {
      setLoading(false);
    }
  };

  const categories = ["Todos", ...new Set(articles.map(a => a.category))];
  
  const filteredArticles = selectedCategory === "Todos" 
    ? articles 
    : articles.filter(a => a.category === selectedCategory);

  return (
    <div data-testid="blog-page" className="min-h-screen bg-[#02040a]">
      <SEO
        title="Blog"
        description="Artículos y noticias sobre tecnología empresarial, sistemas operativos, ERP, CRM y transformación digital por Global Metafora APP."
        path="/blog"
      />
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="section-container">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Blog Técnico
            </span>
            <h1 
              data-testid="blog-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Conocimiento & Expertise
            </h1>
            <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto">
              Artículos, guías y recursos sobre transformación digital, 
              seguridad informática y gestión empresarial.
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`category-${category.toLowerCase()}`}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? "bg-[#3b82f6] text-white"
                    : "bg-[#0b1221] text-[#94a3b8] hover:text-white border border-white/10"
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            /* Articles Grid */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, index) => (
                <motion.article
                  key={article.id}
                  data-testid={`blog-card-${article.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card overflow-hidden group"
                >
                  {/* Image */}
                  <Link to={`/blog/${article.slug}`}>
                    <div className="relative aspect-video overflow-hidden">
                      <LazyImage
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#02040a] to-transparent" />
                      <span className="absolute bottom-4 left-4 bg-[#3b82f6]/90 text-white text-xs font-medium px-2 py-1">
                        {article.category}
                      </span>
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="p-6">
                    <Link to={`/blog/${article.slug}`}>
                      <h2 className="text-xl font-bold text-white mb-3 group-hover:text-[#3b82f6] transition-colors line-clamp-2">
                        {article.title}
                      </h2>
                    </Link>
                    <p className="text-[#94a3b8] text-sm mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-[#94a3b8]">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(article.published_at).toLocaleDateString('es-ES', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {article.read_time} min
                        </span>
                      </div>
                      <Link
                        to={`/blog/${article.slug}`}
                        className="flex items-center gap-1 text-[#3b82f6] hover:gap-2 transition-all"
                      >
                        Leer
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredArticles.length === 0 && (
            <div className="text-center py-20">
              <p className="text-[#94a3b8]">No hay artículos en esta categoría.</p>
            </div>
          )}

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="glass-card p-10 max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-4">
                ¿Tienes un Proyecto en Mente?
              </h2>
              <p className="text-[#94a3b8] mb-6">
                Más de 20 años de experiencia respaldando a empresas como la tuya.
              </p>
              <Link
                to="/#contacto"
                data-testid="blog-cta"
                className="btn-primary inline-block"
              >
                <span>Contáctanos Hoy</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
      <div className="noise-overlay" />
    </div>
  );
};

export default BlogPage;
